import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { confirmMaterialOrder } from '@/lib/api/materialOrders';
import { materialOrderStatus, parseConfirmedQuantity } from '@/lib/productionMaterialPlanning';
export function MaterialOrderConfirmation({ order, onSaved, beforeCloseRef }) {
  const [quantity, setQuantity] = React.useState(String(order.confirmed_quantity ?? order.quantity));
  const [delivery, setDelivery] = React.useState(order.expected_delivery_date || '');
  const [notes, setNotes] = React.useState(order.confirmation_notes || '');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const valid = Number.isFinite(parseConfirmedQuantity(quantity));
  const savingRef = React.useRef(false);
  const initial = React.useRef({ quantity, delivery, notes });
  const save = async event => {
    event?.preventDefault();
    if (savingRef.current) return false;
    if (!valid) { setError('Revisa la cantidad confirmada antes de cerrar.'); return false; }
    savingRef.current = true;
    setBusy(true); setError('');
    try {
      const result = await confirmMaterialOrder(order, parseConfirmedQuantity(quantity), delivery, notes);
      initial.current = { quantity, delivery, notes };
      await onSaved(result);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { savingRef.current = false; setBusy(false); }
  };
  React.useEffect(() => {
    if (!beforeCloseRef) return;
    beforeCloseRef.current = async () => {
      if (savingRef.current) return false;
      const previous = initial.current;
      if (quantity === previous.quantity && delivery === previous.delivery && notes === previous.notes) return true;
      return save();
    };
    return () => { beforeCloseRef.current = null; };
  });
  return <section className="min-w-0 rounded border border-blue-200 bg-blue-50 p-4 space-y-3">
    <h3 className="font-semibold">Confirmación interna · {materialOrderStatus(order)}</h3>
    <p className="text-sm">Solicitado: <b>{order.quantity}</b>. Registra la respuesta del proveedor; solo lo confirmado se asigna al cuadrante. Indica 0 si no puede servirlo. No registra una recepción.</p>
    <form onSubmit={save} className="min-w-0 space-y-3"><fieldset disabled={busy} className="min-w-0 grid gap-3 sm:grid-cols-2 [&>label]:min-w-0">
      <label>Cantidad confirmada<Input name="quantity" type="text" inputMode="decimal" autoComplete="off" required onFocus={e => e.target.select()} value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
      <label>Entrega prevista<Input name="delivery" type="date" value={delivery} onChange={e => setDelivery(e.target.value)} /></label>
      <label className="sm:col-span-2">Observaciones internas<textarea name="notes" className="mt-1 w-full rounded border p-2" maxLength={2000} value={notes} onChange={e => setNotes(e.target.value)} /></label>
    </fieldset><p className="text-xs text-gray-600">Puedes sustituir la cantidad por una mayor o menor; admite decimales con coma o punto. La cantidad solicitada se conserva.</p>{!valid && <p className="text-sm text-amber-800">Introduce una cantidad igual o mayor que 0, con hasta cuatro decimales.</p>}{error && <p role="alert" className="text-red-700">{error}</p>}
      <Button disabled={!valid || busy} type="submit">{busy ? 'Guardando…' : order.confirmed_quantity == null ? 'Confirmar cantidades' : 'Guardar revisión de confirmación'}</Button>
    </form>
    {order.confirmed_at && <p className="text-sm">Última confirmación: {new Date(order.confirmed_at).toLocaleString('es-ES')} · {order.confirmed_quantity} confirmadas de {order.quantity} solicitadas.</p>}
    {order.confirmation_history?.length > 0 && <details><summary>Historial de confirmaciones ({order.confirmation_history.length})</summary>{[...order.confirmation_history].reverse().map((entry, i) => <p className="mt-2 text-sm" key={i}>{new Date(entry.at).toLocaleString('es-ES')} · {entry.quantity} confirmadas · Entrega: {entry.delivery_date || 'Sin fecha'}{entry.notes && ` · ${entry.notes}`}</p>)}</details>}
  </section>;
}
